import type { BrandId } from "./brandRegistry";

export type UbsBrandIconId =
  | "arrow-back"
  | "arrow-next"
  | "arrow-down-filled"
  | "arrow-up"
  | "arrow-down"
  | "arrow-left"
  | "arrow-right"
  | "arrow-move-down"
  | "arrow-move-up"
  | "arrow-move-to-bottom"
  | "arrow-move-up-right"
  | "arrow-slide-back"
  | "arrow-slide-next";

export interface BrandIconCatalogEntry {
  id: string;
  label: string;
  description: string;
  assetPath: string;
}

const UBS_ICON_ASSET_DIR = "assets/brand/ubs/icons";

export const UBS_BRAND_ICON_CATALOG: readonly BrandIconCatalogEntry[] = [
  {
    id: "arrow-right",
    label: "Chevron right",
    description: "Open chevron pointing right; compact LTR step indicator.",
    assetPath: `${UBS_ICON_ASSET_DIR}/arrow-right.png`,
  },
  {
    id: "arrow-left",
    label: "Chevron left",
    description: "Open chevron pointing left.",
    assetPath: `${UBS_ICON_ASSET_DIR}/arrow-left.png`,
  },
  {
    id: "arrow-up",
    label: "Chevron up",
    description: "Open chevron pointing up.",
    assetPath: `${UBS_ICON_ASSET_DIR}/arrow-up.png`,
  },
  {
    id: "arrow-down",
    label: "Chevron down",
    description: "Open chevron pointing down.",
    assetPath: `${UBS_ICON_ASSET_DIR}/arrow-down.png`,
  },
  {
    id: "arrow-back",
    label: "Filled triangle back",
    description: "Filled triangle pointing left (heavier back affordance).",
    assetPath: `${UBS_ICON_ASSET_DIR}/arrow-back.png`,
  },
  {
    id: "arrow-next",
    label: "Filled triangle next",
    description: "Filled triangle pointing right (heavier forward affordance).",
    assetPath: `${UBS_ICON_ASSET_DIR}/arrow-next.png`,
  },
  {
    id: "arrow-down-filled",
    label: "Filled triangle down",
    description: "Filled triangle pointing down.",
    assetPath: `${UBS_ICON_ASSET_DIR}/arrow-down-filled.png`,
  },
  {
    id: "arrow-slide-next",
    label: "Slide connector next",
    description:
      "Shaft + arrowhead pointing right; preferred process-flow connector between stage boxes.",
    assetPath: `${UBS_ICON_ASSET_DIR}/arrow-slide-next.png`,
  },
  {
    id: "arrow-slide-back",
    label: "Slide connector back",
    description: "Shaft + arrowhead pointing left; reverse/back process connector.",
    assetPath: `${UBS_ICON_ASSET_DIR}/arrow-slide-back.png`,
  },
  {
    id: "arrow-move-down",
    label: "Move down",
    description:
      "Compound move-down mark (arrow + handle); use only for downward movement/reordering, not generic flow.",
    assetPath: `${UBS_ICON_ASSET_DIR}/arrow-move-down.png`,
  },
  {
    id: "arrow-move-up",
    label: "Move up",
    description: "Compound move-up mark; same rule as move-down.",
    assetPath: `${UBS_ICON_ASSET_DIR}/arrow-move-up.png`,
  },
  {
    id: "arrow-move-to-bottom",
    label: "Move to bottom",
    description: "Compound to-bottom mark; same rule as move-*.",
    assetPath: `${UBS_ICON_ASSET_DIR}/arrow-move-to-bottom.png`,
  },
  {
    id: "arrow-move-up-right",
    label: "Move up-right",
    description: "Diagonal move-up-right mark; use for branching/up-and-across, not default LTR flow.",
    assetPath: `${UBS_ICON_ASSET_DIR}/arrow-move-up-right.png`,
  },
] as const;

export const UBS_BRAND_ICON_IDS: readonly UbsBrandIconId[] = UBS_BRAND_ICON_CATALOG.map(
  (entry) => entry.id as UbsBrandIconId
);

const UBS_BRAND_ICON_BY_ID = new Map(
  UBS_BRAND_ICON_CATALOG.map((entry) => [entry.id, entry] as const)
);

export function getBrandIconCatalog(
  brandId: BrandId | null | undefined
): readonly BrandIconCatalogEntry[] {
  if (brandId === "ubs") return UBS_BRAND_ICON_CATALOG;
  return [];
}

export function getBrandIconIds(brandId: BrandId | null | undefined): readonly string[] {
  return getBrandIconCatalog(brandId).map((entry) => entry.id);
}

/**
 * Schema-field description for `brandIconId`, generated from the catalog so
 * emit-time glossary text cannot drift from the enum.
 */
export function buildBrandIconSchemaDescription(
  brandId: BrandId | null | undefined
): string | null {
  const catalog = getBrandIconCatalog(brandId);
  if (catalog.length === 0) return null;

  const glossary = catalog
    .map((entry) => `- ${entry.id}: ${entry.description}`)
    .join("\n");

  return [
    "UBS brand icon id. Prefer this over geometricType arrow for process/flow direction.",
    "Runtime resolves to a packaged image; do not invent imageUrl/base64.",
    "Pick by meaning, not by name similarity alone:",
    glossary,
  ].join("\n");
}

export function lookupBrandIcon(
  iconId: string | undefined | null
): BrandIconCatalogEntry | undefined {
  if (typeof iconId !== "string") return undefined;
  const trimmed = iconId.trim();
  if (!trimmed) return undefined;
  return UBS_BRAND_ICON_BY_ID.get(trimmed);
}

/**
 * Fetch a brand icon asset and return a data URI suitable for FactSet image create.
 * Returns null when the id is unknown or the asset cannot be loaded.
 */
export async function resolveBrandIconDataUri(
  iconId: string | undefined | null,
  log?: (message: string) => void
): Promise<string | null> {
  const entry = lookupBrandIcon(iconId);
  if (!entry) {
    log?.(`Unknown brandIconId "${String(iconId ?? "").trim()}"`);
    return null;
  }

  try {
    const response = await fetch(entry.assetPath, { credentials: "omit", mode: "cors" });
    if (!response.ok) {
      log?.(`Brand icon fetch failed (${response.status}) for ${entry.assetPath}`);
      return null;
    }
    const blob = await response.blob();
    const mimeType = (blob.type || "image/png").toLowerCase();
    if (!mimeType.startsWith("image/")) {
      log?.(`Ignoring non-image brand icon payload (${mimeType}) for ${entry.assetPath}`);
      return null;
    }
    const dataUri = await blobToDataUri(blob, mimeType);
    return dataUri;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    log?.(`Could not resolve brand icon ${entry.id}: ${message}`);
    return null;
  }
}

function blobToDataUri(blob: Blob, fallbackMimeType: string): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string" && result.startsWith("data:")) {
        resolve(result);
        return;
      }
      resolve(null);
    };
    reader.onerror = () => resolve(null);
    // Prefer FileReader data URL so mime is preserved from the blob when present.
    if (blob.type) {
      reader.readAsDataURL(blob);
      return;
    }
    // Rare hosts omit blob.type; force PNG data URL via typed wrapper.
    reader.readAsDataURL(new Blob([blob], { type: fallbackMimeType }));
  });
}
